import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import {
  CustomFieldRecord,
  CustomFieldType,
} from "@/shared/domain/customFields";

// Timestamps render in the viewer's zone with the zone abbreviation shown; the
// `z` token needs advancedFormat, and .tz() needs utc + timezone.
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

const TIMESTAMP_DISPLAY_FORMAT = "MMM D, YYYY h:mm A z";

/** The viewer's IANA time zone, used to interpret and label Timestamp values. */
export function guessTimezone(): string {
  return dayjs.tz.guess();
}

/**
 * Wire value for a Timestamp draft. Guards against the invalid dayjs the
 * DateTimePicker hands `onChange` for an out-of-range or half-typed date
 * (e.g. Feb 30): a truthy-but-invalid dayjs whose `.toISOString()` throws
 * `RangeError: Invalid time value`. An invalid input emits `null`, which the
 * timestamp schema rejects — disabling commit rather than crashing on it.
 */
export function timestampToWireValue(next: dayjs.Dayjs | null): string | null {
  return next && next.isValid() ? next.toISOString() : null;
}

/**
 * Renders a custom-field value for the values table, formatted by the field's
 * declared type. Callers pass only non-null values (cleared fields aren't
 * rendered). The table renders Enum values as chips, so this branch is a
 * fallback. `timezone` is injectable so tests can pin it.
 */
export function formatCustomFieldValue(
  field: CustomFieldRecord,
  value: unknown,
  timezone: string = guessTimezone(),
): string {
  switch (field.field_type) {
    case CustomFieldType.Boolean:
      return value ? "True" : "False";
    case CustomFieldType.Timestamp:
      return typeof value === "string" || typeof value === "number"
        ? dayjs(value).tz(timezone).format(TIMESTAMP_DISPLAY_FORMAT)
        : String(value);
    case CustomFieldType.Number:
      return typeof value === "number" ? value.toLocaleString() : String(value);
    case CustomFieldType.String:
    case CustomFieldType.Enum:
    default:
      return String(value);
  }
}
